import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getFeaturedPost,
  getPostBySlug,
  getPostsPage,
  searchPosts,
  type Post,
  type PostsFilter,
  type PostsPage,
} from '@/api-client/posts';

const POSTS_PAGE_SIZE = 12;
/** Short enough that edits (webhook-purged on the CDN) show up quickly */
const STALE_TIME = 60 * 1000;

function nextPageSkip(lastPage: PostsPage): number | undefined {
  const next = lastPage.skip + lastPage.posts.length;
  // An empty page means the list ended even if `total` disagrees (posts unpublished meanwhile)
  return lastPage.posts.length > 0 && next < lastPage.total ? next : undefined;
}

/** Flattens loaded pages, dropping duplicates caused by posts published between page loads. */
function flattenPages(data: InfiniteData<PostsPage> | undefined): Post[] {
  if (!data) return [];
  const seen = new Set<string>();
  const posts: Post[] = [];
  for (const page of data.pages) {
    for (const post of page.posts) {
      if (!seen.has(post.id)) {
        seen.add(post.id);
        posts.push(post);
      }
    }
  }
  return posts;
}

/**
 * Paginated list of the newest posts ("load more"), optionally filtered to a
 * site section or an author on the server.
 * @returns The infinite query plus the flattened `posts` and `total`
 */
export function usePostsInfiniteQuery(filter: PostsFilter = {}) {
  const query = useInfiniteQuery({
    queryKey: ['postsPages', filter],
    queryFn: ({ pageParam }) => getPostsPage(pageParam, POSTS_PAGE_SIZE, filter),
    initialPageParam: 0,
    getNextPageParam: nextPageSkip,
    staleTime: STALE_TIME,
  });
  const posts = useMemo(() => flattenPages(query.data), [query.data]);
  return { ...query, posts, total: query.data?.pages[0]?.total ?? 0 };
}

/**
 * Paginated full-text search. Disabled for queries shorter than 2 characters.
 * @param term - The (trimmed) search term
 */
export function useSearchQuery(term: string) {
  const query = useInfiniteQuery({
    queryKey: ['search', term],
    queryFn: ({ pageParam }) => searchPosts(term, pageParam, POSTS_PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: nextPageSkip,
    enabled: term.length >= 2,
    staleTime: STALE_TIME,
  });
  const posts = useMemo(() => flattenPages(query.data), [query.data]);
  return { ...query, posts, total: query.data?.pages[0]?.total ?? 0 };
}

/** A single post by slug; resolves to `null` when it does not exist (anymore). */
export function usePostBySlugQuery(slug: string) {
  return useQuery({
    queryKey: ['post', slug],
    queryFn: () => getPostBySlug(slug),
    enabled: !!slug,
    staleTime: STALE_TIME, // unpublished posts disappear quickly
  });
}

/** The home page hero post (top featured post, or the newest one). */
export function useFeaturedPostQuery() {
  return useQuery({
    queryKey: ['featuredPost'],
    queryFn: getFeaturedPost,
    staleTime: STALE_TIME,
  });
}
